import type { MockSectionCustomerInput } from './mockSectionData.util';
import { mockSectionData } from './mockSectionData.util';
import type { ReportSectionFieldSources } from './reportFieldSource.util';
import { pickReportFieldValue } from './reportFieldSource.util';
import {
  coerceIllustrativeDistributionFromPctRows,
  coerceWhoWereReachingDistribution,
} from './whoWereReachingDistribution.util';
import {
  coerceWhoWereReachingCounties,
  isLikelyMockWhoWereReachingCounties,
  isWarehouseWhoWereReachingSnapshot,
} from './whoWereReachingSnapshotDetect.util';

/** County row in the who-were-reaching snapshot. */
export interface WhoWereReachingCountySnapshot {
  name: string;
  memberCount: number;
}

const hasCountyData = (
  counties: WhoWereReachingCountySnapshot[],
): boolean => counties.some(county => county.memberCount > 0);

const hasDistributionData = (
  rows: Array<{ value: number }>,
): boolean => rows.some(row => row.value > 0);

/**
 * Normalizes stored who-were-reaching snapshots with mock fallbacks for missing fields.
 * @param snapshot - Stored or live snapshot JSON.
 * @param customer - Customer context for mock fallback fields.
 * @param reportingPeriod - Report period label.
 * @returns Snapshot with merged data and per-field `fieldSources` metadata.
 */
export const normalizeWhoWereReachingSnapshot = (
  snapshot: Record<string, unknown>,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> => {
  const mockBase = mockSectionData(
    'who-were-reaching',
    customer,
    reportingPeriod,
  );

  const usStateCode =
    typeof snapshot.usStateCode === 'string'
      ? snapshot.usStateCode
      : customer.usStateCode ?? mockBase.usStateCode;

  const engagedTotal =
    typeof snapshot.engagedTotal === 'number' &&
    Number.isFinite(snapshot.engagedTotal)
      ? snapshot.engagedTotal
      : mockBase.engagedTotal;

  const stateCodeForCounties =
    typeof usStateCode === 'string' ? usStateCode : customer.usStateCode ?? 'MI';

  const coercedCounties = coerceWhoWereReachingCounties(snapshot.counties);
  const mockCounties = coerceWhoWereReachingCounties(mockBase.counties);

  const warehouseCounties = isWarehouseWhoWereReachingSnapshot(snapshot)
    ? coercedCounties
    : coercedCounties.length > 0 &&
        !isLikelyMockWhoWereReachingCounties(
          coercedCounties,
          stateCodeForCounties,
        )
      ? coercedCounties
      : [];

  const countiesPick = pickReportFieldValue(
    warehouseCounties,
    mockCounties,
    hasCountyData(warehouseCounties),
  );

  const warehouseAge = coerceWhoWereReachingDistribution(snapshot.age);
  const mockAge = coerceIllustrativeDistributionFromPctRows(mockBase.age);
  const agePick = pickReportFieldValue(
    warehouseAge,
    mockAge,
    hasDistributionData(warehouseAge),
  );

  const warehouseRace = coerceWhoWereReachingDistribution(snapshot.raceEthnicity);
  const mockRace = coerceIllustrativeDistributionFromPctRows(
    mockBase.raceEthnicity,
  );
  const racePick = pickReportFieldValue(
    warehouseRace,
    mockRace,
    hasDistributionData(warehouseRace),
  );

  const engagedPick = pickReportFieldValue(
    engagedTotal,
    mockBase.engagedTotal,
    isWarehouseWhoWereReachingSnapshot(snapshot) &&
      typeof snapshot.engagedTotal === 'number' &&
      snapshot.engagedTotal > 0,
  );

  const fieldSources: ReportSectionFieldSources = {
    counties: countiesPick.source,
    age: agePick.source,
    raceEthnicity: racePick.source,
    engagedTotal: engagedPick.source,
  };

  return {
    ...mockBase,
    ...snapshot,
    usStateCode,
    engagedTotal: engagedPick.value,
    counties: countiesPick.value,
    age: agePick.value,
    raceEthnicity: racePick.value,
    fieldSources,
  };
};

export {
  coerceWhoWereReachingCounties,
  coerceWhoWereReachingMemberCount,
  isWarehouseWhoWereReachingSnapshot,
  WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
} from './whoWereReachingSnapshotDetect.util';
